import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Client } from 'src/providers/client/client';
import { Addresses } from 'src/providers/customer/addresses';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { buildGuidebookUrl } from 'src/shared/util/guidebook-url';

import {
  AddressGuidebookService,
  AddressSetting,
  AddressWithSettings,
  CUSTOM_SECTION_KEY_PREFIX,
  GUIDEBOOK_V2_CATEGORIES,
  GuidebookV2Category,
  GuidebookV2CategoryId,
  isCustomSectionKey,
  isPredefinedV2Key,
  ReservationSummary,
  SECTION_ORDER_KEY,
  slugifyCustomLabel,
  UpsertGuidebookEntry
} from 'src/shared/services/address-guidebook.service';
import { normalizeCategoryOrder, RETIRED_ACCESS_KEYS } from 'src/shared/services/guidebook-entries';
import { guidebookAddressField, isRentalAccount } from 'src/shared/services/guidebook-address-fields';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

const GUIDEBOOK_BASE_URL = 'https://guidebook.tidy.com';
const DEMO_PREVIEW_TOKEN = 'test-guidebook';

const GUIDEBOOK_KEYS = {
  welcome_message: 'welcome_message',
  check_in_instructions: 'check_in_instructions',
  check_out_instructions: 'check_out_instructions',
  house_rules: 'house_rules',
  emergency_contact: 'emergency_contact',
  contact_collection_enabled: 'contact_collection_enabled',
  identity_verification_enabled: 'identity_verification_enabled',
} as const;

const CATEGORY_VISIBILITY_PREFIX = 'category_visible_';

const ADDRESS_MODEL_FIELDS = [
  'home_access',
  'home_close',
  'wifi_network',
  'wifi_password',
  'parking_notes',
  'parking_spot',
  'paid_parking',
  'parking_pay_with',
  'max_parking_cost',
  'guest_home_access',
  'guest_parking_notes',
  'guest_parking_spot',
  'guest_paid_parking',
  'guest_parking_pay_with',
  'guest_max_parking_cost',
] as const;

type AddressModelField = (typeof ADDRESS_MODEL_FIELDS)[number];

@Component({
  selector: 'guidebook-config-editor',
  templateUrl: './guidebook-config-editor.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class GuidebookConfigEditorComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private addressGuidebookService = inject(AddressGuidebookService);
  private addresses = inject(Addresses);
  private client = inject(Client);
  private sanitizer = inject(DomSanitizer);

  readonly windowService = inject(WindowService);

  // Rental accounts edit the guest parking / access columns here.
  private readonly isRental = isRentalAccount();

  addressId: number | null = null;
  propertyName = 'Property';
  isDataLoaded = false;
  isRightSideContent = false;
  fromGettingStarted = false;
  errorMessage: string;
  submitted = false;
  form: FormGroup;
  previewUrl: string | null = null;
  safePreviewUrl: SafeResourceUrl | null = null;
  previewReservation: ReservationSummary | null = null;
  isDemoPreview = false;

  /** Ordered category list — mutated in-place on drag-drop to persist owner ordering. */
  categories: GuidebookV2Category[] = [...GUIDEBOOK_V2_CATEGORIES];

  /** Tracks per-category collapse state. Collapsed by default to keep the editor short. */
  collapsedCategories: Record<GuidebookV2CategoryId, boolean> = {
    parking: true,
    access: true,
    connectivity: true,
    stay_details: true,
    emergencies: true,
    contacts: true,
    property_reference: true,
    appliances: true,
    checkout: true,
  };

  /**
   * Pre-built map of field key → FormControl reference, used in the template to avoid
   * function calls per Angular best practices.
   */
  fieldControls: Record<string, FormControl> = {};

  /** Pre-built map of category id → per-category visibility FormControl. */
  categoryVisibilityControls: Record<string, FormControl> = {};

  /** Snapshot of AddressModel values loaded from the backend, used for dirty-tracking on save. */
  private initialAddressValues: Partial<Record<AddressModelField, unknown>> = {};

  boundSave = () => this.save();
  boundOpenPreview = () => this.openPreview();
  boundOpenDemoPreview = () => this.openDemoPreview();
  boundAddCustomSection = () => this.addCustomSection();

  get customSections(): FormArray {
    return this.form.get('custom_sections') as FormArray;
  }

  get fieldsGroup(): FormGroup {
    return this.form.get('fields') as FormGroup;
  }

  get categoryVisibilityGroup(): FormGroup {
    return this.form.get('category_visible') as FormGroup;
  }

  toggleCategory(id: GuidebookV2CategoryId) {
    this.collapsedCategories[id] = !this.collapsedCategories[id];
    this.cdr.markForCheck();
  }

  onSectionsReordered(reordered: GuidebookV2Category[]) {
    this.categories = reordered;
    this.cdr.markForCheck();
  }

  addCustomSection() {
    this.customSections.push(this.buildCustomSectionGroup({ key: '', label: '', value: '', visible: true }));
    this.cdr.markForCheck();
  }

  removeCustomSection(index: number) {
    const group = this.customSections.at(index) as FormGroup;
    const key = group.get('key')?.value as string;
    if (key) {
      group.get('deleted')?.setValue(true);
      group.disable({ emitEvent: false });
    } else {
      this.customSections.removeAt(index);
    }
    this.cdr.markForCheck();
  }

  private buildCustomSectionGroup(entry: { key: string; label: string; value: string; visible: boolean }): FormGroup {
    return this.fb.group({
      key: [entry.key],
      label: [entry.label],
      value: [entry.value],
      visible: [entry.visible],
      deleted: [false],
    });
  }

  async ngOnInit() {
    try {
      this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
      const params = await this.storage.retrieve('dialog-params');
      this.addressId = params?.addressId || null;
      this.fromGettingStarted = !!params?.fromGettingStarted;

      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogPageTitle('Edit Guidebook Content');
      }

      this.buildForm();

      if (this.addressId) {
        await this.loadAddressSettings(this.addressId);
      } else {
        this.isDataLoaded = true;
        this.cdr.markForCheck();
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load guidebook editor';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  private buildForm() {
    const fieldsControls: Record<string, FormControl> = {};
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      for (const field of category.fields) {
        fieldsControls[field.key] = this.fb.control('');
      }
    }

    const categoryVisibilityControls: Record<string, FormControl> = {};
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      categoryVisibilityControls[category.id] = this.fb.control(true);
    }

    this.form = this.fb.group({
      welcome_message: [''],
      welcome_message_visible: [true],
      check_in_instructions: [''],
      check_in_instructions_visible: [true],
      house_rules: [''],
      house_rules_visible: [true],
      contact_collection_enabled: [false],
      identity_verification_enabled: [false],
      fields: this.fb.group(fieldsControls),
      category_visible: this.fb.group(categoryVisibilityControls),
      custom_sections: this.fb.array([]),
    });

    this.fieldControls = fieldsControls;
    this.categoryVisibilityControls = categoryVisibilityControls;
  }

  private async loadAddressSettings(addressId: number) {
    try {
      const address = await this.addressGuidebookService.getAddressWithSettings(addressId);
      this.propertyName = address.address_name || address.address1 || 'Property';
      this.patchFormFromAddress(address);
      this.applyStoredCategoryOrder(address.address_settings || []);
      await this.loadPreviewUrl(addressId);
    } catch {
      this.errorMessage = 'Failed to load guidebook configuration';
    }
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private async loadPreviewUrl(addressId: number) {
    try {
      const reservations = await this.addressGuidebookService.listReservationsForAddress(addressId, 'active');
      const picked = this.pickPreviewReservation(reservations);
      this.previewReservation = picked;
      this.previewUrl = picked?.uuid ? this.buildPreviewUrl(picked.uuid) : null;
      this.isDemoPreview = false;
      this.safePreviewUrl = this.previewUrl
        ? this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl)
        : null;
    } catch {
      this.previewReservation = null;
      this.previewUrl = null;
      this.safePreviewUrl = null;
      this.isDemoPreview = false;
    }
  }

  /**
   * Owners can preview the guidebook before any guest books by loading the renderer's
   * built-in mock data via the demo token.
   */
  loadDemoPreview() {
    this.previewUrl = this.buildPreviewUrl(DEMO_PREVIEW_TOKEN);
    this.safePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl);
    this.isDemoPreview = true;
    this.cdr.markForCheck();
  }

  openDemoPreview() {
    const url = this.buildPreviewUrl(DEMO_PREVIEW_TOKEN);
    this.util.openUrl(url);
  }

  private buildPreviewUrl(uuid: string): string {
    return buildGuidebookUrl(uuid);
  }

  private pickPreviewReservation(reservations: ReservationSummary[]): ReservationSummary | null {
    const withUuid = (reservations || []).filter(r => !!r.uuid);
    if (withUuid.length === 0) {return null;}
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = withUuid
      .filter(r => (r.check_in_date || '') >= today)
      .sort((a, b) => (a.check_in_date || '').localeCompare(b.check_in_date || ''));
    if (upcoming.length > 0) {return upcoming[0];}
    return withUuid.sort((a, b) => (b.check_in_date || '').localeCompare(a.check_in_date || ''))[0];
  }

  openPreview() {
    if (!this.previewUrl) {return;}
    this.util.openUrl(this.previewUrl);
  }

  private patchFormFromAddress(address: AddressWithSettings) {
    const settings = address.address_settings || [];
    const byKey = this.addressGuidebookService.toMap(settings);

    const readString = (key: string) => {
      const value = byKey[key]?.['value'];
      return typeof value === 'string' ? value : '';
    };

    const readBool = (key: string) => {
      const value = byKey[key]?.['value'];
      return typeof value === 'boolean' ? value : false;
    };

    const readVisibility = (key: string, fallback: boolean) => {
      const flag = byKey[key]?.['visible_to_guest'];
      return typeof flag === 'boolean' ? flag : fallback;
    };

    this.form.patchValue({
      welcome_message: readString(GUIDEBOOK_KEYS.welcome_message),
      welcome_message_visible: readVisibility(GUIDEBOOK_KEYS.welcome_message, true),
      check_in_instructions: readString(GUIDEBOOK_KEYS.check_in_instructions),
      check_in_instructions_visible: readVisibility(GUIDEBOOK_KEYS.check_in_instructions, true),
      house_rules: readString(GUIDEBOOK_KEYS.house_rules),
      house_rules_visible: readVisibility(GUIDEBOOK_KEYS.house_rules, true),
      contact_collection_enabled: readBool(GUIDEBOOK_KEYS.contact_collection_enabled),
      identity_verification_enabled: readBool(GUIDEBOOK_KEYS.identity_verification_enabled),
    });

    // Per-category visibility: prefer the stored category_visible_<id> setting (the renderer
    // hides the whole category group, including AddressModel-backed fields, when it is false),
    // falling back to the predefined fields' visibility for data saved before it was stored.
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      const predefinedField = category.fields.find(field => !field.fromAddressModel);
      const fallback = predefinedField ? readVisibility(predefinedField.key, true) : true;
      const flag = byKey[`${CATEGORY_VISIBILITY_PREFIX}${category.id}`]?.['value'];
      const visible = category.fixed || (typeof flag === 'boolean' ? flag : fallback);
      this.categoryVisibilityGroup.controls[category.id]?.setValue(visible, { emitEvent: false });
    }

    // Patch per-field values. AddressModel fields read from the address record;
    // address_settings fields read from the byKey map.
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      for (const field of category.fields) {
        const control = this.fieldsGroup.controls[field.key];
        if (!control) {continue;}
        if (field.fromAddressModel && field.addressField) {
          const value = address[guidebookAddressField(field.addressField, this.isRental)];
          control.setValue(value ?? '', { emitEvent: false });
        } else {
          control.setValue(readString(field.key), { emitEvent: false });
        }
      }
    }

    // Snapshot AddressModel values for dirty-tracking on save.
    this.initialAddressValues = {};
    for (const addrField of ADDRESS_MODEL_FIELDS) {
      this.initialAddressValues[addrField] = address[addrField];
    }

    // Custom (free-form) sections — anything custom_* that is NOT a predefined v2 key.
    this.customSections.clear({ emitEvent: false });
    for (const setting of settings) {
      if (!isCustomSectionKey(setting.key)) {continue;}
      if (isPredefinedV2Key(setting.key)) {continue;}
      const data = setting.data || {};
      if (data['deleted'] === true) {continue;}
      const label = typeof data['label'] === 'string' ? (data['label'] as string) : this.deriveLabelFromKey(setting.key);
      const value = typeof data['value'] === 'string' ? (data['value'] as string) : '';
      // Empty slots of the old Access card aren't sections; skipping them also stops saves rewriting them.
      if (RETIRED_ACCESS_KEYS.has(setting.key) && value.trim() === '') {continue;}
      const visible = typeof data['visible_to_guest'] === 'boolean' ? (data['visible_to_guest'] as boolean) : true;
      this.customSections.push(this.buildCustomSectionGroup({ key: setting.key, label, value, visible }));
    }
  }

  private applyStoredCategoryOrder(settings: AddressSetting[]) {
    const orderSetting = settings.find(s => s.key === SECTION_ORDER_KEY);
    const storedOrder = orderSetting?.data?.['value'];
    if (!Array.isArray(storedOrder)) {return;}
    // Shared with the guidebook page, which slots categories added later into place.
    this.categories = normalizeCategoryOrder(storedOrder)
      .map(id => GUIDEBOOK_V2_CATEGORIES.find(category => category.id === id))
      .filter((category): category is GuidebookV2Category => !!category);
  }

  private deriveLabelFromKey(key: string): string {
    const raw = key.startsWith(CUSTOM_SECTION_KEY_PREFIX) ? key.slice(CUSTOM_SECTION_KEY_PREFIX.length) : key;
    return raw.replace(/_+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  async save() {
    this.submitted = true;
    if (!this.addressId) {
      this.util.showError('Missing property reference. Please reopen the guidebook.', 5000);
      return;
    }
    try {
      const settingsEntries = this.buildUpsertEntries(this.form.value);
      const addressPatch = this.buildAddressModelPatch();

      // Save sequentially, guidebook settings first. If that PUT fails we must not persist the
      // AddressModel patch — otherwise the property record and the guidebook diverge into a
      // half-saved state (the address keeps the new home_access/wifi/parking while no setting saved).
      const updatedAddress = await this.addressGuidebookService.upsertGuidebook(this.addressId, settingsEntries);
      // Deletions come after the upsert for the same reason: if the PUT failed
      // we must not have already destroyed the rows it was going to rewrite.
      await this.addressGuidebookService.deleteGuidebookSettings(this.addressId, this.deletedCustomKeys);
      if (Object.keys(addressPatch).length > 0) {
        await this.addresses.updateAddress(addressPatch, this.addressId);
      }
      this.patchFormFromAddress(updatedAddress);

      if (this.fromGettingStarted) {
        await this.markOnboardingStepComplete();
      }
      this.util.showSuccess('Guidebook content saved', 3000);
      this.goBack();
    } catch {
      this.util.showError('Failed to save guidebook content', 3000);
    } finally {
      this.cdr.markForCheck();
    }
  }

  /** Returns only the AddressModel fields that changed since last load. */
  private buildAddressModelPatch(): Partial<Record<AddressModelField, unknown>> {
    const patch: Partial<Record<AddressModelField, unknown>> = {};
    const fieldsValue = this.fieldsGroup.value as Record<string, unknown>;

    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      for (const field of category.fields) {
        // Selects aren't editable here; the guidebook page owns the parking choices.
        if (!field.fromAddressModel || !field.addressField || field.inputType === 'select') {continue;}
        const column = guidebookAddressField(field.addressField, this.isRental) as AddressModelField;
        const next = fieldsValue[field.key];
        const initial = this.initialAddressValues[column];
        const normalizedNext = next === '' ? null : next;
        const normalizedInitial = initial === '' || initial === undefined ? null : initial;
        if (normalizedNext !== normalizedInitial) {
          patch[column] = next ?? null;
        }
      }
    }
    return patch;
  }

  /**
   * Keys of already-persisted custom sections the user deleted this session,
   * collected while building the upsert entries and flushed by `save()`.
   */
  private deletedCustomKeys: string[] = [];

  private buildUpsertEntries(formValue: Record<string, unknown>): UpsertGuidebookEntry[] {
    const entries: UpsertGuidebookEntry[] = [];
    this.deletedCustomKeys = [];

    // Top-of-editor fields with per-field visibility.
    const topLevelFields: { field: keyof typeof GUIDEBOOK_KEYS; visibleControl: string }[] = [
      { field: 'welcome_message',         visibleControl: 'welcome_message_visible' },
      { field: 'check_in_instructions',   visibleControl: 'check_in_instructions_visible' },
      { field: 'house_rules',             visibleControl: 'house_rules_visible' },
    ];
    for (const { field, visibleControl } of topLevelFields) {
      entries.push({
        key: GUIDEBOOK_KEYS[field],
        data: {
          value: formValue[field] ?? '',
          visible_to_guest: !!formValue[visibleControl],
        },
      });
    }

    // Guest settings.
    entries.push({ key: GUIDEBOOK_KEYS.contact_collection_enabled, data: { value: !!formValue['contact_collection_enabled'] } });
    entries.push({ key: GUIDEBOOK_KEYS.identity_verification_enabled, data: { value: !!formValue['identity_verification_enabled'] } });

    // Per-category visibility. Persisted as category_visible_<id> settings so the renderer can
    // hide the whole category group (including AddressModel-backed fields like home_access),
    // and mirrored onto each field's visible_to_guest below.
    const categoryVisibility = (formValue['category_visible'] as Record<string, boolean>) || {};
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      entries.push({
        key: `${CATEGORY_VISIBILITY_PREFIX}${category.id}`,
        data: { value: categoryVisibility[category.id] !== false },
      });
    }

    // Predefined v2 fields backed by address_settings (skip AddressModel-backed fields).
    const fieldsValue = (formValue['fields'] as Record<string, unknown>) || {};
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      const categoryVisible = categoryVisibility[category.id] !== false;
      for (const field of category.fields) {
        if (field.fromAddressModel) {continue;}
        entries.push({
          key: field.key,
          data: {
            label: field.label,
            value: fieldsValue[field.key] ?? '',
            visible_to_guest: categoryVisible,
            category: category.id,
          },
        });
      }
    }

    // Ad-hoc custom sections.
    const customRows = this.customSections.getRawValue() as Array<{
      key: string;
      label: string;
      value: string;
      visible: boolean;
      deleted: boolean;
    }>;
    const usedKeys = new Set<string>();
    for (const row of customRows) {
      const label = (row.label || '').trim();
      const value = row.value || '';
      if (!row.key && !label && !value && !row.deleted) {continue;}

      let key = row.key;
      if (!key) {
        key = slugifyCustomLabel(label || `section_${Date.now()}`);
        while (usedKeys.has(key)) {
          key = `${key}_${Math.floor(Math.random() * 1000)}`;
        }
      }
      usedKeys.add(key);

      // A deleted section is removed via the DELETE endpoint rather than
      // upserted with a null payload — `data` is now required on every entry.
      // A row the user added and deleted in the same session was never
      // persisted, so there's nothing to delete.
      if (row.deleted) {
        if (row.key) { this.deletedCustomKeys.push(key); }
        continue;
      }

      entries.push({
        key,
        data: {
          label,
          value,
          visible_to_guest: !!row.visible,
        },
      });
    }

    // Owner-defined section order.
    entries.push({
      key: SECTION_ORDER_KEY,
      data: { value: this.categories.map(c => c.id) },
    });

    return entries;
  }

  private async markOnboardingStepComplete() {
    try {
      await this.client.addMetadata({
        key: `onboarding_step.${this.addressId}.guidebook`,
        value: 'completed',
      });
    } catch {
      // Non-fatal — completion will reconcile on next poll.
    }
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('guidebook');
    }
  }

}
